import api from "../axiosConfig";

const PAYMENT_ENDPOINTS = {
  PAYMENTS: "/payments",
  PAYMENT_BY_ID: (id) => `/payments/${id}`,
  PAYMENT_STATS: "/payments/stats",
};

// Get all payments with optional filters
export const getAllPayments = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();

    // Add filter parameters
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== "") {
        queryParams.append(key, value);
      }
    });

    const response = await api.get(
      `${PAYMENT_ENDPOINTS.PAYMENTS}${
        queryParams.toString() ? `?${queryParams}` : ""
      }`
    );

    return {
      success: true,
      data: response.data.data || response.data,
      pagination: response.data.pagination,
      message: "Payments loaded successfully",
    };
  } catch (error) {
    console.error("Error fetching payments:", error);
    return {
      success: false,
      data: [],
      error:
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch payments",
    };
  }
};

// Get payment by ID
export const getPaymentById = async (paymentId) => {
  try {
    const response = await api.get(PAYMENT_ENDPOINTS.PAYMENT_BY_ID(paymentId));

    return {
      success: true,
      data: response.data.data || response.data,
      message: "Payment loaded successfully",
    };
  } catch (error) {
    console.error("Error fetching payment:", error);
    return {
      success: false,
      data: null,
      error:
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch payment",
    };
  }
};

// Create new payment
export const createPayment = async (paymentData) => {
  try {
    const response = await api.post(PAYMENT_ENDPOINTS.PAYMENTS, paymentData);

    return {
      success: true,
      data: response.data.data || response.data,
      message: "Payment created successfully",
    };
  } catch (error) {
    console.error("Error creating payment:", error);
    return {
      success: false,
      data: null,
      error:
        error.response?.data?.message ||
        error.message ||
        "Failed to create payment",
    };
  }
};

// Update payment
export const updatePayment = async (paymentId, paymentData) => {
  try {
    const response = await api.put(
      PAYMENT_ENDPOINTS.PAYMENT_BY_ID(paymentId),
      paymentData
    );

    return {
      success: true,
      data: response.data.data || response.data,
      message: "Payment updated successfully",
    };
  } catch (error) {
    console.error("Error updating payment:", error);
    return {
      success: false,
      data: null,
      error:
        error.response?.data?.message ||
        error.message ||
        "Failed to update payment",
    };
  }
};

// Delete payment
export const deletePayment = async (paymentId) => {
  try {
    await api.delete(PAYMENT_ENDPOINTS.PAYMENT_BY_ID(paymentId));

    return {
      success: true,
      data: { id: paymentId },
      message: "Payment deleted successfully",
    };
  } catch (error) {
    console.error("Error deleting payment:", error);
    return {
      success: false,
      data: null,
      error:
        error.response?.data?.message ||
        error.message ||
        "Failed to delete payment",
    };
  }
};

// Get customer payments. GET /payments has no customer filter, so list the
// org's payments and filter by customerId client-side.
export const getCustomerPayments = async (customerId) => {
  try {
    const response = await api.get(PAYMENT_ENDPOINTS.PAYMENTS);
    const body = response.data;
    const all = Array.isArray(body) ? body : body?.data || [];
    const idOf = (v) => (v && typeof v === "object" ? v._id || v.id : v);
    const data = all.filter((pmt) => String(idOf(pmt.customerId)) === String(customerId));

    return {
      success: true,
      data,
      message: "Customer payments loaded successfully",
    };
  } catch (error) {
    console.error("Error fetching customer payments:", error);
    return {
      success: false,
      data: [],
      error:
        error.response?.data?.message ||
        error.message ||
        error.error ||
        "Failed to fetch customer payments",
    };
  }
};

// Vendor payments: the /payments routes are receivables only and there is no
// backend endpoint listing vendor payments, so fail clearly.
export const getVendorPayments = async () => ({
  success: false,
  data: [],
  error: "Vendor payments are not available from the server",
});

// Get payment statistics
export const getPaymentStats = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== "") {
        queryParams.append(key, value);
      }
    });

    const response = await api.get(
      `${PAYMENT_ENDPOINTS.PAYMENT_STATS}${
        queryParams.toString() ? `?${queryParams}` : ""
      }`
    );

    return {
      success: true,
      data: response.data.data || response.data,
      message: "Payment statistics loaded successfully",
    };
  } catch (error) {
    console.error("Error fetching payment stats:", error);
    return {
      success: false,
      data: {
        totalPayments: 0,
        totalAmount: 0,
        customerPayments: 0,
        vendorPayments: 0,
        completedPayments: 0,
        pendingPayments: 0,
        monthlyTotal: 0,
        weeklyTotal: 0,
      },
      error:
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch payment statistics",
    };
  }
};

// No bulk endpoint exists on the backend (POST /payments records one); fail clearly.
export const processBulkPayments = async () => ({
  success: false,
  data: null,
  error: "Bulk payments are not supported by the server",
});

// Record payment for invoice
export const recordInvoicePayment = async (invoiceId, paymentData) => {
  try {
    const response = await api.post(PAYMENT_ENDPOINTS.PAYMENTS, {
      ...paymentData,
      invoiceId,
    });

    return {
      success: true,
      data: response.data.data || response.data,
      message: "Invoice payment recorded successfully",
    };
  } catch (error) {
    console.error("Error recording invoice payment:", error);
    return {
      success: false,
      data: null,
      error:
        error.response?.data?.message ||
        error.message ||
        "Failed to record invoice payment",
    };
  }
};

// Get payments by invoice
export const getPaymentsByInvoice = async (invoiceId) => {
  try {
    const response = await api.get(`/payments/invoice/${invoiceId}`);

    return {
      success: true,
      data: response.data.data || response.data,
      message: "Invoice payments loaded successfully",
    };
  } catch (error) {
    console.error("Error fetching invoice payments:", error);
    return {
      success: false,
      data: [],
      error:
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch invoice payments",
    };
  }
};

// Alias for compatibility with existing code
export const getInvoicePayments = getPaymentsByInvoice;

// Alias for compatibility with existing code
export const getPayment = getPaymentById;

// Export payment data (CSV/Excel)
export const exportPayments = async (filters = {}, format = "csv") => {
  try {
    const queryParams = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== "") {
        queryParams.append(key, value);
      }
    });

    queryParams.append("format", format);

    const response = await api.get(`/payments/export?${queryParams}`, {
      responseType: "blob",
    });

    return {
      success: true,
      data: response.data,
      message: "Payment data exported successfully",
    };
  } catch (error) {
    console.error("Error exporting payments:", error);
    return {
      success: false,
      data: null,
      error:
        error.response?.data?.message ||
        error.message ||
        "Failed to export payment data",
    };
  }
};

// Payment service object with all methods
export const paymentService = {
  getAllPayments,
  getPaymentById,
  getPayment: getPaymentById, // Alias
  createPayment,
  updatePayment,
  deletePayment,
  getCustomerPayments,
  getVendorPayments,
  getPaymentStats,
  processBulkPayments,
  recordInvoicePayment,
  getPaymentsByInvoice,
  getInvoicePayments: getPaymentsByInvoice, // Alias
  exportPayments,
};

export default paymentService;
